/**
 * https://ui.shadcn.com/docs/components/calendar
 */
"use client";
import { useEffect, useState } from "react";
import type { DateInterval } from "react-day-picker";
import { Temporal } from "temporal-polyfill";
import { Calendar } from "./calendar";
import { Icon } from "./icon";

interface DatePickerProps {
  minDate?: Temporal.PlainDate;
  maxDate?: Temporal.PlainDate;
  selectedDate?: Temporal.PlainDate;
  onDateSelected: (value: Temporal.PlainDate) => void;
}

export function DatePicker({ minDate, maxDate, selectedDate, onDateSelected }: DatePickerProps) {
  const [date, setDate] = useState<Date | undefined>();

  // Convert Temporal date to JavaScript Date object for the Calendar component
  useEffect(() => {
    if (!selectedDate) {
      setDate(undefined);
      return;
    }

    setDate(new Date(selectedDate.year, selectedDate.month - 1, selectedDate.day));
  }, [selectedDate]);

  const handleSelect = (selected: Date | undefined) => {
    setDate(selected);

    if (selected) {
      // Convert back to Temporal.PlainDate for the callback
      onDateSelected(
        Temporal.PlainDate.from({
          year: selected.getFullYear(),
          month: selected.getMonth() + 1,
          day: selected.getDate(),
        }),
      );
    }
  };

  const disabledDays = Object.fromEntries(
    Object.entries({
      after: maxDate ? new Date(maxDate.year, maxDate.month - 1, maxDate.day) : undefined,
      before: minDate ? new Date(minDate.year, minDate.month - 1, minDate.day) : undefined,
    }).filter(([_, value]) => value != null),
  ) as DateInterval;

  return (
    <div>
      <Calendar
        initialFocus
        mode="single"
        defaultMonth={date}
        selected={date}
        onSelect={handleSelect}
        numberOfMonths={1}
        disabled={disabledDays}
        className="w-full p-4"
        formatters={{
          formatWeekdayName: (date) => date.toLocaleString("en-US", { weekday: "short" }),
        }}
        classNames={{
          months: "w-full",
          month: "w-full",
          caption: "flex items-center justify-center gap-1 mb-8 relative",
          caption_label: "text-sm font-semibold text-textPrimary",
          nav: "flex absolute left-2 right-2 justify-between",
          nav_button: "h-7 w-7 bg-transparent p-0 hover:opacity-50",
          nav_button_previous: "",
          nav_button_next: "",
          head: "flex",
          head_row: "flex justify-between w-full",
          head_cell: "flex-1 flex items-center justify-center relative p-0 text-xs font-normal",
          row: "flex w-full mt-2",
          cell: "flex-1 flex items-center justify-center relative p-0",
          day: "h-9 w-9 p-0 font-semibold relative flex items-center justify-center",
          day_selected: "bg-black text-white hover:bg-black hover:text-white rounded-full",
          day_today: "border border-black rounded-full",
          day_outside: "text-gray-300 opacity-50",
          day_disabled: "opacity-50",
          day_hidden: "invisible",
        }}
        components={{
          IconLeft: () => <Icon name="arrow-left" iconSize="16px" />,
          IconRight: () => <Icon name="arrow-right" iconSize="16px" />,
        }}
      />
    </div>
  );
}
